/* eslint-disable no-console */
import axiosStatic, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { format } from 'components/atoms/input-duration/formatter';
import { toastService } from 'components/molecules/toast';
import { API_HOST, API_NAMESPACE, API_VERSION } from 'config/environment';
import { ApiKey, logger, CustomerPreview } from 'services';
import { logout } from 'utils/logout';
import { addHopIdToHeader } from 'utils/window-utils';

const EXCLUDE_LOGOUT_PATHS = ['/setup-profile', '/login', '/2fa'];

const config: InternalAxiosRequestConfig<any> = {
  baseURL: `${API_HOST}/${API_NAMESPACE}/${API_VERSION}`,
  adapter: 'fetch',
  headers: new AxiosHeaders(),
};

const withCustomHeader = (
  header: Record<string, string>,
  configuration = config,
): InternalAxiosRequestConfig<any> => ({
  ...configuration,
  headers: configuration.headers.set(header),
});

const isMethodAllowedInPreview = (method = ''): boolean =>
  /(get|post)/i.test(method);

const injectApiKey = (configuration: InternalAxiosRequestConfig<any>) => {
  return Promise.resolve(withCustomHeader(ApiKey.header(), configuration));
};

const injectPreviewApiKey = async (
  configuration: InternalAxiosRequestConfig<any>,
) => {
  return Promise.resolve(
    withCustomHeader(CustomerPreview.header(), configuration),
  );
};

const removeCookieIfPreview = async (
  configuration: InternalAxiosRequestConfig<any>,
) => {
  const [isPreviewMode] = await CustomerPreview.isPreviewMode();

  if (!isPreviewMode) {
    return Promise.resolve(configuration);
  }

  return Promise.resolve({
    ...configuration,
    fetchOptions: { credentials: 'omit' },
  });
};

const injectHopId = (configuration: InternalAxiosRequestConfig<any>) =>
  Promise.resolve(withCustomHeader(addHopIdToHeader(), configuration));

const rejectWriteRequestsInPreview = async (
  configuration: InternalAxiosRequestConfig<any>,
) => {
  if (isMethodAllowedInPreview(configuration?.method)) {
    return Promise.resolve(configuration);
  }
  const [preview] = await CustomerPreview.isPreviewMode();

  if (preview) {
    throw new Error('editing disallowed in preview mode');
  }

  return Promise.resolve(configuration);
};

enum ApiErrorCode {
  InvalidKey = 'INVALID_KEY',
  NotAuthorized = 'NOT_AUTHORIZED',
}

enum ApiStatusCode {
  NotAuthorized = 401,
  TooManyRequests = 429,
}

export const axios = (): Observable<AxiosInstance> =>
  ApiKey.tokens().pipe(
    map(() => axiosStatic.create(withCustomHeader(ApiKey.header(), config))),
    tap((client) => client.interceptors.request.use(injectPreviewApiKey)),
    tap((client) => client.interceptors.request.use(injectApiKey)),
    tap((client) => client.interceptors.request.use(removeCookieIfPreview)),
    tap((client) => client.interceptors.request.use(injectHopId)),
    tap((client) =>
      client.interceptors.request.use(rejectWriteRequestsInPreview),
    ),
    tap((client) =>
      client.interceptors.response.use(
        (response) => response,
        (error: AxiosError<any>) => {
          // Printing axios errors in console for debugging
          console.log('error', error);
          if (
            error.response?.status === ApiStatusCode.NotAuthorized &&
            (error.response?.data?.errors?.[0]?.errorCode ===
              ApiErrorCode.NotAuthorized ||
              error.response?.data?.[0]?.errorCode ===
                ApiErrorCode.NotAuthorized)
          ) {
            logger.error('response failed with 401, logging out', { error });
            if (!EXCLUDE_LOGOUT_PATHS.includes(window.location.pathname)) {
              logout();
            }
          } else if (error.response?.status === ApiStatusCode.TooManyRequests) {
            const retryAt = error.response?.headers['x-retry-after'];

            toastService({
              title: 'Too many requests!',
              message: `You have exceeded the request limit.${
                retryAt ? ` Please try again in ${format('m', retryAt)}.` : ''
              }`,
              duration: 10,
            }).error();
          }

          return Promise.reject(error);
        },
      ),
    ),
    shareReplay(1),
  );
